import React from 'react';
import axios from 'axios';
import { Card } from 'reactstrap';
import { Button, Label, SearchInput, Switch, Tag } from '@jem-open/jem-ui';
import Swal from 'sweetalert2';
import moment from 'moment';
import debounce from 'lodash.debounce';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import { environment } from '../../../environment/environment';
import { DataTable, sortableHeader } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { Pager } from '../../../components/pager';

class EmployersList extends React.Component {
  state = {
    employers: [],
    loading: false,
    query: '',
    show_deleted: false,
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  };

  componentDidMount() {
    this.getEmployers();
    this.search('');
  }

  deleteEmployer(id, name, deleting = true) {
    Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure you want to ${
        deleting ? 'delete' : 'undelete'
      } ${name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, ${deleting ? 'delete' : 'undelete'} it!`,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${environment.baseUrl}/employers/${id}/`)
          .then((res) => {
            Swal.fire({
              title: `${name} ${deleting ? 'deleted' : 'undeleted'}`,
              icon: 'success',
            });
            this.getEmployers();
          })
          .catch((error) => {
            apiResponseHandler.handleApiError('Error', error);
          });
      }
    });
  }

  getEmployers() {
    this.setState({ loading: true });
    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    axios
      .get(`${environment.baseUrl}/employers/`, {
        headers,
        params: { pagination: true, page: this.state.currentPage },
      })
      .then((res) => {
        this.setState({
          employers: res.data.results,
          totalPages: res.data.pages,
          totalCount: res.data.count,
          loading: false,
        });
      });
  }

  onChange = (e) => {
    const { value } = e.target;
    this.setState({
      query: value,
      currentPage: 1,
    });

    this.debouncedSearch(value);
  };

  onClear = () => {
    this.setState({ query: '', currentPage: 1 });
    this.debouncedSearch('');
  };

  search = (query) => {
    this.setState({ loading: true });
    axios
      .get(`${environment.baseUrl}/employers/`, {
        params: {
          search: query,
          pagination: true,
          page: this.state.currentPage,
        },
      })
      .then((res) => {
        this.setState({
          employers: res.data.results,
          totalPages: res.data.pages,
          totalCount: res.data.count,
          loading: false,
        });
      });
  };

  debouncedSearch = debounce((query) => {
    this.search(query);
  }, 500);

  setPage = (page) => {
    this.setState({ currentPage: page }, () => {
      if (this.state.query) {
        this.search(this.state.query);
      } else {
        this.getEmployers();
      }
    });
  };

  compareByAsc(accessor) {
    const getValue =
      typeof accessor === 'function' ? accessor : (item) => item[accessor];
    return function (a, b) {
      if (getValue(a) < getValue(b)) return -1;
      if (getValue(a) > getValue(b)) return 1;
      return 0;
    };
  }

  compareByDesc(accessor) {
    const getValue =
      typeof accessor === 'function' ? accessor : (item) => item[accessor];
    return function (a, b) {
      if (getValue(a) < getValue(b)) return 1;
      if (getValue(a) > getValue(b)) return -1;
      return 0;
    };
  }

  sortBy(accessor) {
    const arrayCopy = [...this.state.employers];
    const arrInStr = JSON.stringify(arrayCopy);
    arrayCopy.sort(this.compareByAsc(accessor));
    const arrInStr1 = JSON.stringify(arrayCopy);
    if (arrInStr === arrInStr1) {
      arrayCopy.sort(this.compareByDesc(accessor));
    }
    this.setState({ employers: arrayCopy });
  }

  handleSort = (accessor) => this.sortBy(accessor);

  getDefaultPayRoll = (employerPayCycles) => {
    if (employerPayCycles.length) {
      const defaultPayCycle = employerPayCycles.find(
        (payCycle) => payCycle.is_default
      );

      if (defaultPayCycle) {
        return defaultPayCycle.name;
      }
    }
    return '';
  };

  getDefaultFeeModel = (subscriptions, transactionFees) => {
    const defaultSubscription = subscriptions.find((sub) => sub.default);
    if (defaultSubscription) return defaultSubscription.name;

    const defaultTransactionFee = transactionFees.find((txn) => txn.is_default);
    if (defaultTransactionFee)
      return `(${defaultTransactionFee.id}) ${defaultTransactionFee.fee_type} - R${defaultTransactionFee.fee_amount} + ${defaultTransactionFee.fee_percentage}%`;

    return '';
  };

  render() {
    const { employers, loading, show_deleted, currentPage, totalPages } =
      this.state;
    const visibleEmployers = employers.filter((employer) => {
      if (show_deleted) {
        return employer;
        // eslint-disable-next-line no-else-return
      } else {
        return employer.status === 'active';
      }
    });
    const columns = [
      {
        key: 'company_name',
        label: sortableHeader('Company', 'company_name', this.handleSort),
        width: '220px',
        render: (value) => (
          <div className="max-w-[200px] truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        key: 'status',
        label: sortableHeader('Status', 'status', this.handleSort),
        render: (value, row) => {
          if (row.status === 'active')
            return <Tag variant="success">Active</Tag>;
          if (row.status === 'deleted')
            return <Tag variant="failed">Deleted</Tag>;
          return row.status ? <Tag variant="neutral">{row.status}</Tag> : null;
        },
      },
      {
        key: 'fee_model',
        label: sortableHeader(
          'Default fee model',
          (row) =>
            this.getDefaultFeeModel(row.subscriptions, row.transaction_fees),
          this.handleSort
        ),
        width: '240px',
        render: (value, row) => {
          const feeModel = this.getDefaultFeeModel(
            row.subscriptions,
            row.transaction_fees
          );
          return (
            <div className="max-w-[220px] truncate" title={feeModel}>
              {feeModel}
            </div>
          );
        },
      },
      {
        key: 'pay_cycles',
        label: sortableHeader(
          'Default pay cycle',
          (row) => this.getDefaultPayRoll(row.pay_cycles),
          this.handleSort
        ),
        width: '200px',
        render: (value, row) => {
          const payCycle = this.getDefaultPayRoll(row.pay_cycles);
          return (
            <div className="max-w-[180px] truncate" title={payCycle}>
              {payCycle}
            </div>
          );
        },
      },
      {
        key: 'default_risk_metric',
        label: sortableHeader(
          'Default risk metric',
          'default_risk_metric',
          this.handleSort
        ),
        render: (value) => `${value}%`,
      },
      {
        key: 'date_created',
        label: sortableHeader('Date created', 'date_created', this.handleSort),
        render: (value) => moment(value).format('DD MMM YY HH:mm'),
      },
      {
        key: 'actions',
        label: <span className="normal-case">Actions</span>,
        width: '225px',
        render: (value, row) => (
          <div className="flex items-center gap-xs">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                this.props.history.push(`/admin/employers/${row.id}`)
              }>
              View
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                this.props.history.push('/admin/advance_batches_configurations')
              }>
              Batch Configuration
            </Button>
          </div>
        ),
      },
    ];

    const emptyState = (
      <EmptyState icon="fas fa-building" message="No employers found" />
    );

    return (
      <div className="min-h-screen bg-white p-md">
        <div
          className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
          style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
          <div>
            <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
              Employers
            </h1>
            <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
              View and manage employers and their configuration.
            </p>
          </div>
        </div>
        <div className="mb-md flex flex-wrap items-center gap-sm">
          <div className="min-w-[280px] max-w-md flex-1">
            <SearchInput
              value={this.state.query}
              placeholder="Search Employers"
              onChange={this.onChange}
              onClear={this.onClear}
            />
          </div>
          <div className="flex items-center gap-xs">
            <Switch
              id="show-deleted-toggle"
              checked={show_deleted}
              onCheckedChange={(checked) =>
                this.setState({ show_deleted: checked })
              }
            />
            <Label htmlFor="show-deleted-toggle">Show deleted</Label>
          </div>
          <Button
            variant="primary"
            className="ml-auto"
            onClick={() => this.props.history.push('/admin/employers/add')}>
            Add New
          </Button>
        </div>
        <Card className="shadow border-0">
          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={visibleEmployers}
              loading={loading}
              emptyState={emptyState}
              skeletonRows={8}
            />
          </div>
        </Card>

        {totalPages > 1 && (
          <div className="mt-md flex justify-center">
            <Pager
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={this.setPage}
            />
          </div>
        )}
      </div>
    );
  }
}

export default EmployersList;
